import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Copy, DollarSign, Users, CheckCircle2, Clock, LogOut, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { referralService } from '@/services/referralService';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { usePartnerSession } from '@/hooks/usePartnerSession';
import SEO from '@/components/SEO';

export default function AffiliatePortal() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { partner, isLoading: sessionLoading, isAuthenticated, login, logout } = usePartnerSession();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [processingLoginToken, setProcessingLoginToken] = useState(false);

  // Detect login token immediately on mount
  useEffect(() => {
    const loginToken = searchParams.get('login_token');
    if (loginToken) {
      console.log('[AffiliatePortal] Login token detected in URL');
      setProcessingLoginToken(true);
    }
  }, []);

  // Handle magic link login
  useEffect(() => {
    const loginToken = searchParams.get('login_token');
    console.log('[AffiliatePortal] Login effect:', { 
      hasToken: !!loginToken, 
      isAuthenticated, 
      sessionLoading,
      processingLoginToken 
    });
    
    if (loginToken && !isAuthenticated && !sessionLoading) {
      console.log('[AffiliatePortal] Calling login() with token...');
      login(loginToken).then((success) => {
        console.log('[AffiliatePortal] Login result:', success);
        setProcessingLoginToken(false);
        if (success) {
          // Clear the token from URL
          searchParams.delete('login_token');
          setSearchParams(searchParams, { replace: true });
          toast.success('Welcome back!');
        } else {
          setLoginError('Invalid or expired login link. Please request a new one.');
        }
      });
    }
  }, [searchParams, isAuthenticated, sessionLoading, login, setSearchParams]);

  // Redirect if not authenticated after loading (but not if processing a login token)
  useEffect(() => {
    const hasLoginToken = searchParams.get('login_token');
    console.log('[AffiliatePortal] Redirect check:', { 
      sessionLoading, 
      isAuthenticated, 
      hasLoginToken: !!hasLoginToken,
      processingLoginToken 
    });
    
    if (!sessionLoading && !isAuthenticated && !hasLoginToken && !processingLoginToken) {
      console.log('[AffiliatePortal] Redirecting to /partner-login');
      navigate('/partner-login');
    }
  }, [sessionLoading, isAuthenticated, searchParams, processingLoginToken, navigate]);

  const referralCode = partner?.referralCode || '';

  const { data: events } = useQuery({
    queryKey: ['my-referral-events', referralCode],
    queryFn: async () => {
      if (!referralCode) return [];
      const { data, error } = await supabase
        .from('referral_events')
        .select(`
          *,
          referral_types (display_name)
        `)
        .eq('referral_code', referralCode)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data;
    },
    enabled: !!referralCode && isAuthenticated
  });

  const { data: payouts } = useQuery({
    queryKey: ['my-payouts', partner?.id],
    queryFn: async () => {
      if (!partner?.id) return [];
      const { data, error } = await supabase
        .from('referral_payouts')
        .select('*')
        .eq('referral_source_id', partner.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!partner?.id && isAuthenticated
  });

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/partner-login');
  };

  // Show loading state (including when processing login token)
  if (sessionLoading || processingLoginToken) {
    return (
      <div className="container mx-auto py-16 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">
          {processingLoginToken ? 'Logging you in...' : 'Loading...'}
        </p>
      </div>
    );
  }

  // Show login error
  if (loginError) {
    return (
      <div className="container mx-auto py-16 text-center">
        <h1 className="text-3xl font-bold mb-4">Login Failed</h1>
        <p className="text-muted-foreground mb-6">{loginError}</p>
        <Button onClick={() => navigate('/partner-login')}>
          Request New Login Link
        </Button>
      </div>
    );
  }

  // Show nothing while redirecting
  if (!isAuthenticated) {
    return null;
  }

  const totalReferrals = events?.length || 0;
  const verifiedCount = events?.filter(e => e.status === 'verified' || e.status === 'paid').length || 0;
  const pendingCount = events?.filter(e => e.status === 'pending').length || 0;
  const totalEarnings = events?.filter(e => e.status === 'verified' || e.status === 'paid')
    .reduce((sum, e) => sum + Number(e.commission_amount), 0) || 0;
  const pendingEarnings = events?.filter(e => e.status === 'pending')
    .reduce((sum, e) => sum + Number(e.commission_amount), 0) || 0;

  const referralLink = referralService.generateWelcomeLink(referralCode);

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied!');
  };

  return (
    <>
      <SEO
        title="Affiliate Dashboard - Partner Portal"
        description="Track your SeniorThrive referrals and earnings."
        noindex
      />
      <div className="container mx-auto py-8 px-4">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Affiliate Portal</h1>
            <p className="text-muted-foreground">Welcome back, {partner?.name}!</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to={`/partner-getting-started?code=${referralCode}&link=${encodeURIComponent(referralLink)}`}>
                <BookOpen className="w-4 h-4 mr-2" />
                Getting Started
              </Link>
            </Button>
            <Button variant="ghost" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Total Referrals</p>
                <p className="text-2xl font-bold">{totalReferrals}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Verified</p>
                <p className="text-2xl font-bold">{verifiedCount}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold">${totalEarnings.toFixed(2)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">${pendingEarnings.toFixed(2)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Referral Link */}
        <Card className="p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">Your Referral Link</h2>
          <div className="flex gap-2">
            <Input
              readOnly
              value={referralLink}
              className="flex-1"
            />
            <Button onClick={copyLink}>
              <Copy className="w-4 h-4 mr-2" /> Copy
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Share this link to invite people to SeniorThrive. They'll choose their path (Older Adult or Family Member) and you'll earn based on which plan they select.
          </p>
          <div className="mt-3 text-xs text-muted-foreground">
            <span className="font-medium">Earnings:</span> 20% for ThriveMax (Older Adults) • 25% for ThriveFamily (Family Members)
          </div>
        </Card>

        {/* Recent Events */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">Recent Referrals</h2>
          {events && events.length > 0 ? (
            <div className="space-y-3">
              {events.map((event) => (
                <div key={event.id} className="flex justify-between items-center border-b pb-3">
                  <div>
                    <p className="font-medium">{event.referral_types?.display_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(event.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">${Number(event.commission_amount).toFixed(2)}</p>
                    <p className={`text-sm capitalize ${
                      event.status === 'verified' || event.status === 'paid' ? 'text-green-500' :
                      event.status === 'pending' ? 'text-yellow-500' : 'text-red-500'
                    }`}>
                      {event.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              No referrals yet. Share your link to start earning!
            </p>
          )}
        </Card>
      </div>
    </>
  );
}
